import {
  Box,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { Grid } from '@mui/system';
import { FormattedMessage } from 'react-intl';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';

// Optional: if you want strongly typed columns later
// type YourRow = { name: string; age: number; ... };

interface SkeletonTableProps {
  columns: ColumnDef<any, any>[];
  count?: number;
  pagination?: boolean; // if false → show pagination skeleton
}

export default function SkeletonTable({
  columns,
  count = 10,
  pagination = true,
}: SkeletonTableProps) {
  // Fake empty data for skeleton rows
  const skeletonData = Array.from({ length: count }, () => ({}));

  const table = useReactTable({
    data: skeletonData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // You can add getSortedRowModel etc. later when real
  });

  return (
    <Stack>
      <Table>
        <TableHead sx={{ borderTopWidth: 2 }}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell key={header.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 'max-content' }}>
                      {header.isPlaceholder ? null : (
                        // Most common: string or function returning string/id for i18n
                        <FormattedMessage id={flexRender(header.column.columnDef.header, header.getContext()) as string} />
                      )}
                    </Box>
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>

        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  <Skeleton sx={{ height: 30 }} animation="wave" variant="rounded" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider />

      {!pagination && (
        <Box sx={{ p: 2, py: 2 }}>
          <Grid container alignItems="center" justifyContent="space-between" sx={{ width: 'auto' }}>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
              <Skeleton sx={{ height: 30, width: 162 }} animation="wave" variant="rounded" />
            </Grid>
            <Grid
              size={{ xs: 6, sm: 6, md: 6, lg: 6 }}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <Skeleton sx={{ height: 30, width: 162 }} animation="wave" variant="rounded" />
            </Grid>
          </Grid>
        </Box>
      )}
    </Stack>
  );
}